var _ = require("../../uam/module.js")(module.exports, {
  namespace: "egt.exploit.thankyou",
  title: "EGT Exploit Thank You",
  description: "The /exploit/thank-you arrival page — a buyer's own preorder, resolved from an opaque token.",
  supportServer: true,
  fields: {}
});

var S = require("../helpers/egt.sections.js");

/*!
 * /exploit/thank-you — WHERE A SUCCESSFUL PREORDER LANDS.
 *
 * Replaces the inline success overlay that used to sit on top of the checkout
 * wizard. That overlay was a dismissable box on a page whose job was to take
 * money; this is a destination the buyer can return to, bookmark, and read
 * properly. Founder: "Direct successful purchases to a new custom designed thank
 * you page, that also shows the information about their preorder."
 *
 * THE TONE IS ARRIVAL, NOT RECEIPT. This is the best moment the buyer will ever
 * have with this brand — they have just committed $75 to a book by an author
 * most of them have not read, on the strength of the argument the rest of the
 * site made. The page opens by telling them what they now ARE ("You're one of
 * The Named"), and only then, further down, what they bought. The order details
 * are real and complete, but they are the second thing, not the first.
 *
 * THE PAGE IS STATIC; THE ORDER IS NOT.
 * Every EGT surface is prebuilt to app/cache/app/pages/ and served as bytes, so
 * nothing buyer-specific can be rendered here at build time. What ships is a
 * scaffold of empty slots, marked `data-ty-*`, which app/public/src/
 * egt.exploit.thankyou.js fills from POST /api/exploit/receipt. Three states are
 * present in the markup from the start and swapped by that client:
 *
 *   [data-ty-state="loading"]   what a visitor sees for the ~100ms of the fetch
 *   [data-ty-state="order"]     the real thing
 *   [data-ty-state="missing"]   a stale, mistyped or absent token
 *
 * THE MISSING STATE IS NOT AN ERROR PAGE. A token that no longer resolves is the
 * ordinary end of a link's life, not a fault — the buyer most likely has a valid
 * order and an old URL. It says so warmly, points at Contact, and never mentions
 * tokens, expiry mechanics, or anything that reads as a system failure.
 *
 * NO DARK SCRIM OVER THE MEDIA (agents.md §4). Contrast comes from the frosted
 * card alone; the artwork underneath is never dimmed.
 */
/* NAV CTA, 2026-08-21: the bar used to carry "Pre-order Exploit" →
 * /exploit/preorder. The preorder is off every public surface and that page now
 * 404s (app/routes/egt.exploit.hidden.js), so the nav here points at
 * the free opening like every other Exploit surface. The ORDER CONTENT of this
 * page is deliberately untouched — it is a real buyer's record, not marketing. */
exports.render = async function render() {
  return S.page("/exploit/free", [
    renderPreorder(),
    renderNext(),
    renderClose()
  ], { navCta: { label: "Read the opening free", href: "/exploit/free" } });
};

/* A slot the client fills. Rendered with its own placeholder text rather than
 * empty, so the scaffold has real height before the fetch resolves and the card
 * does not visibly grow under the reader. */
function slot(key, cls, placeholder) {
  return { n: "span", c: cls, "data-ty-slot": key, i: placeholder || "" };
}

/* S.glass() builds the frosted card but returns a fixed shape — it deliberately
 * takes no arbitrary attributes, and it is shared by every surface on the site,
 * so it is not the place to add a pass-through. Decorate the node it returns
 * instead: same card, same classes, plus the state hooks this page needs. */
function withAttrs(node, attrs) {
  Object.keys(attrs).forEach(function (k) { node[k] = attrs[k]; });
  return node;
}

/* ── THE PREORDER SECTION — arrival AND receipt, one integrated card ───────
 * F14b (founder, 2026-07-24): "Combine section 1 and section 2 … so it doesn't
 * look redundant and the receipt is integrated." F14 had left two stacked
 * sections — an arrival card that showed the Named name + certificate, then a
 * separate order card whose receipt header restated BOTH — so the page printed
 * the certificate and the name twice. They are now one card: the celebration,
 * the Named name, the certificate and the itemised receipt read as one
 * continuous statement, each fact appearing exactly once.
 *
 * ONE SECTION, THREE STATES. The three cards below are the same three states
 * the page has always had — loading (the shipped default + no-JS), order (the
 * resolved receipt), and missing (a dead link) — now co-located in a single
 * `.egt-canvas` band over the war-green sphere so the swap never moves a
 * neighbouring card. The client (app/public/src/egt.exploit.thankyou.js)
 * toggles `data-ty-state` to show exactly one at a time.
 *
 * The Named name is the hero: Playfair, display size, gold — the thing the
 * buyer chose, now permanent, and the reason a preorder costs what a
 * post-launch copy will. Everything else on the card supports it.
 *
 * NO DARK SCRIM OVER THE MEDIA (agents.md §4). Contrast comes from the frosted
 * card alone; the artwork underneath is never dimmed. */
function renderPreorder() {
  return {
    n: "section", c: "egt-section egt-canvas egt-canvas--center egt-ty__arrival egt-ty__order",
    id: "your-preorder",
    i: [
      S.media({ img: "/imgs/exploit/war-green-sphere.jpg", alt: "", parallax: 0.08, priority: true }),
      { n: "div", c: "egt__shell egt-canvas__inner",
        i: [ renderLoadingCard(), renderOrderCard(), renderMissingCard() ] }
    ]
  };
}

/* THE NEUTRAL / LOADING CARD — what ships in the HTML, what a no-JS visitor
 * keeps, and what stays up while the fetch is in flight.
 *
 * It cannot be the personalised card: that one says "You're one of The Named"
 * beside a specific name, and rendered before the data arrives — or to a
 * crawler — it asserts something about a visitor we cannot identify. So the
 * page opens neutral and only becomes personal once the server confirms whose
 * order this is. Every line here is true for anyone, and it carries the ship
 * window onto the page in every state. */
function renderLoadingCard() {
  return withAttrs(S.glass({ width: "wide", tone: "dark", c: "egt-ty__card egt-ty__card--arrival",
    content: [
      { n: "p", c: "egt__eyebrow", i: "EXPLOIT — Book One of The Universe" },
      { n: "h1", c: "egt-ty__title", i: [ "Thank ", { n: "span", c: "egt__gold", i: "you." } ] },

      { n: "p", c: "egt-ty__lead",
        i: "The Named Edition of Exploit is the first-edition hardback, printed to order — with a certificate of authenticity issued to one reader and no one else, and a name entered among The Named, the recorded first readers of this world." },
      { n: "p", c: "egt-ty__ships",
        i: [ { n: "strong", i: "Named Edition preorders ship by the end of August 2026." }, " Each one is printed to order, and we email you as it moves." ] },

      /* The slot the receipt will replace once the server answers. No-JS
       * visitors keep the honest "it's in your email" below it instead. */
      { n: "p", c: "egt-ty__loading", role: "status", i: "Bringing up your preorder…" },
      { n: "noscript",
        i: { n: "p", c: "egt-ty__lead",
          i: "Your preorder details are in the confirmation email sent to you when the order was placed." } }
    ] }), { "data-ty-state": "loading" });
}

/* THE INTEGRATED ORDER CARD — revealed only once the server has answered.
 *
 * The celebration (the Named name + the certificate) and the receipt (date,
 * buyer, line items, totals, ship-to) live in ONE card here. Each identifier
 * appears exactly once: the Named name in its display block, the certificate
 * in its block, and the receipt meta below carries only what those two do not
 * — the order date and the buyer's name. This is also what prints (the
 * @media print block reflows THIS card to ink-on-white), so the printed page
 * is a complete receipt: it already carries the two identifiers a receipt most
 * needs, above the line items, without restating anything.
 *
 * NO HANDLING ROW (founder ruling 2026-07-21 — handling is folded into the unit
 * price; CA SB 478 and the MN/CO/MA/OR/VA all-in rules exempt shipping but not
 * handling). The server does not send one. Do not add one. */
function renderOrderCard() {
  return withAttrs(S.glass({ width: "wide", tone: "dark", c: "egt-ty__card egt-ty__card--arrival",
    content: [
      { n: "p", c: "egt__eyebrow", i: "EXPLOIT — Book One of The Universe" },
      { n: "h1", c: "egt-ty__title",
        i: [ "You're one of ", { n: "span", c: "egt__gold", i: "The Named." } ] },

      /* THE NAME — its single occurrence on the page. Not a form field echoed
       * back, a declaration. */
      { n: "div", c: "egt-ty__named",
        i: [
          { n: "p", c: "egt-ty__named-k", i: "Recorded as" },
          { n: "p", c: "egt-ty__named-v", i: slot("namedName", "egt-ty__named-text", "—") }
        ] },

      { n: "p", c: "egt-ty__lead",
        i: "Your name is entered among the first readers of this world, and it stays there — long after the book is out and the window has closed. Your certificate is being printed and will be mailed to you with the hardback." },

      /* THE CERTIFICATE — its single occurrence. Called "certificate", never
       * "certificate number": the value is a unique identifier, and naming it a
       * number invites the buyer to read it as a rank. It is deliberately a guid
       * for that reason (app/helpers/exploit.reserve.js,
       * finalizeSuccessfulPaymentAsync). Do not add "#", do not add a count, do
       * not sort or compare it to anyone else's. */
      { n: "div", c: "egt-ty__cert", "data-ty-cert": "true",
        i: [
          { n: "p", c: "egt-ty__cert-k", i: "Your certificate" },
          { n: "p", c: "egt-ty__cert-v", i: slot("certificate", "egt-ty__cert-id", "—") },
          { n: "p", c: "egt-ty__cert-note",
            i: "A certificate of authenticity carrying this identifier, printed and mailed by the studio. No one else is issued yours." }
        ] },

      { n: "p", c: "egt-ty__ships",
        i: [ { n: "strong", i: "Your book ships by the end of August 2026." }, " It is printed to order, and we will email you as it moves." ] },

      /* THE RECEIPT — the same card, continued. The heading bridges the
       * celebration above into the itemised order below. */
      { n: "h2", c: "egt-ty__h2 egt-ty__receipt-h", i: "Here's what you bought." },

      /* Receipt meta carries ONLY the two facts not already stated above: the
       * order date and who the order is for. The Named name and the certificate
       * are the display blocks higher in this same card — restating them here
       * is exactly the redundancy F14b removed. */
      { n: "dl", c: "egt-ty__receipt-meta",
        i: [
          { n: "div", c: "egt-ty__meta-row",
            i: [ { n: "dt", i: "Order date" }, { n: "dd", i: slot("placedOn", "", "—") } ] },
          { n: "div", c: "egt-ty__meta-row",
            i: [ { n: "dt", i: "Order for" }, { n: "dd", i: slot("buyerName", "", "—") } ] }
        ] },

      { n: "div", c: "egt-ty__grid",
        i: [
          /* what + what it cost */
          { n: "div", c: "egt-ty__panel",
            i: [
              { n: "h3", c: "egt-ty__panel-h", i: "The order" },
              { n: "ul", c: "egt-ty__lines", "data-ty-lines": "true", i: [] },
              { n: "dl", c: "egt-ty__totals",
                i: [
                  { n: "div", c: "egt-ty__total-row",
                    i: [ { n: "dt", i: "Subtotal" }, { n: "dd", i: slot("subtotal", "", "—") } ] },
                  { n: "div", c: "egt-ty__total-row",
                    i: [ { n: "dt", i: "Shipping" }, { n: "dd", i: slot("shipping", "", "—") } ] },
                  /* OH SALES TAX — hidden by default; the client reveals + fills
                   * it ONLY when tax was charged (preorder.taxable). A non-OH or
                   * legacy order never shows this row, so subtotal + shipping ===
                   * total reads exactly as before. Tax is ESTIMATED and finalized
                   * per Ohio law. */
                  { n: "div", c: "egt-ty__total-row", "data-ty-tax-row": "true", hidden: true,
                    i: [ { n: "dt", "data-ty-slot": "taxLabel", i: "Sales tax" }, { n: "dd", i: slot("tax", "", "—") } ] },
                  { n: "div", c: "egt-ty__total-row egt-ty__total-row--sum",
                    i: [ { n: "dt", i: "Paid today" }, { n: "dd", i: slot("total", "egt-ty__total-v", "—") } ] }
                ] }
            ] },

          /* where it goes */
          { n: "div", c: "egt-ty__panel",
            i: [
              { n: "h3", c: "egt-ty__panel-h", i: "Ships to" },
              { n: "address", c: "egt-ty__address", "data-ty-address": "true", i: [] },
              { n: "p", c: "egt-ty__panel-note",
                i: [ { n: "strong", i: "Arriving by the end of August 2026." }, " United States only for the first edition." ] },
              { n: "p", c: "egt-ty__panel-note egt-ty__panel-note--email",
                i: [ "Confirmation sent to ", slot("email", "egt-ty__email", "your inbox"), "." ] }
            ] }
        ] },

      /* Test-mode banner. Present in the markup and revealed only when the
       * record says so, because a test order that LOOKS real is how a founder
       * demo turns into a support ticket. */
      { n: "p", c: "egt-ty__testbadge", "data-ty-test": "true", hidden: "hidden",
        i: "Test mode — no money moved and no book will be printed for this order." },

      /* PRINT / SAVE. No PDF dependency — the browser's own print dialog makes
       * the receipt (Save as PDF is a target there). A real <button>, wired by
       * the client via addEventListener (never an inline onclick — the CSP
       * forbids inline handlers). Hidden from the printed page itself by the
       * @media print block. */
      { n: "div", c: "egt-ty__receipt-actions",
        i: { n: "button", type: "button", c: "egt-cta egt-cta--ghost egt-ty__print",
          "data-ty-print": "true", i: "Print / save receipt" } }
    ] }), { "data-ty-state": "order", hidden: "hidden" });
}

/* THE MISSING CARD — a stale or unreadable link. Warm, blameless, and it offers
 * the one thing that actually helps: a way to reach a human. It must never
 * explain the token, and it must never read as a fault. */
function renderMissingCard() {
  return withAttrs(S.glass({ width: "wide", tone: "dark", c: "egt-ty__card egt-ty__card--missing",
    content: [
      { n: "p", c: "egt__eyebrow", i: "This link" },
      { n: "h2", c: "egt-ty__h2", i: "We can't bring up a preorder from this link." },
      { n: "p", c: "egt-ty__lead",
        i: "Links to this page don't stay open forever. If you've preordered, nothing is wrong with your order — your confirmation email has your certificate, your place among The Named, and where your book is going." },
      { n: "p", c: "egt-ty__lead",
        i: "If you can't find that email, or something doesn't look right, tell us and we'll sort it out." },
      { n: "div", c: "egt-ty__actions",
        i: [
          { n: "a", c: "egt-cta egt-cta--primary", href: "/contact-us", i: "Contact the studio" },
          { n: "a", c: "egt-cta egt-cta--ghost", href: "/exploit", i: "Back to Exploit" }
        ] }
    ] }), { "data-ty-state": "missing", hidden: "hidden" });
}

/* ── §03 WHAT HAPPENS NEXT ────────────────────────────────────────────────
 * Plain language, in order, with no dates we have not committed to. Three
 * beats only — a longer list reads as a process, and the buyer just wants to
 * know that someone is doing something.
 *
 * Shown in every state, including `missing`: it is true for anyone who has
 * preordered, and it is the answer to the question a lost link provokes. */
function renderNext() {
  var steps = [
    [ "Right now",
      "A confirmation is on its way to your inbox with everything on this page. Keep it — it's your record of the order." ],
    [ "Before it ships",
      "Your certificate of authenticity is printed and your name is entered among The Named. Your hardback is printed to order, one at a time." ],
    /* "your shipping address", not "the address above" — this section renders in
     * the missing state too, where there is no address above it to point at. */
    [ "By the end of August 2026",
      "Your book and your certificate are mailed to your shipping address. We'll email you when it's on its way. If anything slips, you'll hear it from us before the date passes — and you can wait or cancel for a full refund." ]
  ];

  return {
    n: "section", c: "egt-section egt-ty__next",
    i: { n: "div", c: "egt__shell",
      i: [
        { n: "p", c: "egt__eyebrow", i: "What happens next" },
        { n: "h2", c: "egt-section__title", i: "You don't need to do anything." },
        { n: "ol", c: "egt-ty__steps",
          i: steps.map(function (s) {
            return { n: "li", c: "egt-ty__step",
              i: [
                { n: "p", c: "egt-ty__step-k", i: s[0] },
                { n: "p", c: "egt-ty__step-v", i: s[1] }
              ] };
          }) }
      ] }
  };
}

/* ── §04 CLOSE ────────────────────────────────────────────────────────────
 * The music is free and always has been — it is the one thing we can give the
 * buyer to do RIGHT NOW, in the gap between paying and receiving, and it is the
 * honest close for this page. No upsell: they have already bought the thing. */
function renderClose() {
  return {
    n: "section", c: "egt-section egt-canvas egt-canvas--center egt-ty__close",
    i: [
      S.media({ img: "/imgs/exploit/exploit-hero-bg.jpg", alt: "", parallax: 0.06 }),
      { n: "div", c: "egt__shell egt-canvas__inner",
        i: S.glass({ width: "narrow", tone: "dark", c: "egt-ty__card",
          content: [
            { n: "p", c: "egt__eyebrow", i: "While you wait" },
            { n: "h2", c: "egt-ty__h2", i: "The world is already playing." },
            { n: "p", c: "egt-ty__lead",
              i: "Three original albums were written alongside this book. They're free, they've always been free, and they're the fastest way into the world you just bought a piece of." },
            { n: "div", c: "egt-ty__actions",
              i: [
                /* /entertainment#wall is where the albums actually are — the
                 * album wall section id (app/elements/egt.entertainment.js).
                 * /exploit has no listen anchor; do not invent one here. */
                { n: "a", c: "egt-cta egt-cta--primary", href: "/entertainment#wall", i: "Listen now" },
                { n: "a", c: "egt-cta egt-cta--ghost", href: "/contact-us", i: "Contact the studio" }
              ] }
          ] }) }
    ]
  };
}
